import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api, DashboardTemplateItem, TenantItem, TenantDetail, CluvioDashboardInfo, FilterOption, ProfileSearchItem, UserListItem, UserDetail } from './client';

export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const resp = await api.get<{ data: { listDashboardTemplates: { items: DashboardTemplateItem[] } } }>('/templates');
      return resp.data.listDashboardTemplates.items;
    },
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['template', id],
    queryFn: async () => {
      const resp = await api.get<{ data: { getDashboardTemplate: DashboardTemplateItem } }>(`/templates/${id}`);
      return resp.data.getDashboardTemplate;
    },
    enabled: !!id,
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Record<string, unknown> }) => {
      return api.put<{ data: { updateDashboardTemplate: { id: string; name: string } } }>(`/templates/${id}`, input);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.invalidateQueries({ queryKey: ['template', vars.id] });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return api.del(`/templates/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      return api.post<{ data: { createDashboardTemplate: { id: string; name: string } } }>('/templates', input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

export function useTenants() {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const resp = await api.get<{ data: { listTenants: { items: TenantItem[] } } }>('/tenants');
      return resp.data.listTenants.items;
    },
  });
}

export function useTenant(id: string) {
  return useQuery({
    queryKey: ['tenant', id],
    queryFn: async () => {
      const resp = await api.get<{ data: { getTenant: TenantDetail } }>(`/tenants/${id}`);
      return resp.data.getTenant;
    },
    enabled: !!id,
  });
}

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      return api.post<{ data: { encodeDashboard: { id: string; name: string; url: string } } }>('/dashboards', input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    // auditLabel lets the BFF record a human-readable name — dashboards have
    // no getDashboard query, so the server can't look it up after deletion.
    mutationFn: async ({ id, auditLabel }: { id: string; auditLabel?: string }) => {
      return api.del(`/dashboards/${id}`, auditLabel ? { _auditLabel: auditLabel } : undefined);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['tenant'] });
    },
  });
}

export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string; name?: string; description?: string; sortOrder?: number; _auditLabel?: string }) => {
      return api.put(`/dashboards/${id}`, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant'] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useProfileSearch(search: string) {
  return useQuery({
    queryKey: ['profile-search', search],
    queryFn: async () => {
      const resp = await api.get<{ data: { listProfiles: { items: ProfileSearchItem[] } } }>(
        `/profiles?search=${encodeURIComponent(search)}`
      );
      return resp.data.listProfiles.items;
    },
    enabled: search.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useAddMember(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { profileID: string; tenantRole: string; typeOfTenantID: string }) => {
      return api.post(`/tenants/${tenantId}/members`, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });
}

export function useInviteMember(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; firstname: string; lastname: string; tenantRole: string; typeOfTenantID: string }) => {
      return api.post(`/tenants/${tenantId}/invite`, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });
}

export function useRemoveMember(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (membershipId: string) => {
      return api.del(`/memberships/${membershipId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });
}

export function useUpdateMemberRole(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, tenantRole }: { id: string; tenantRole: string }) => {
      return api.put(`/memberships/${id}`, { tenantRole });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
  });
}

export function useCluvioDashboards() {
  return useQuery({
    queryKey: ['cluvio-dashboards'],
    queryFn: async () => {
      const resp = await api.get<{ dashboards: CluvioDashboardInfo[] }>('/cluvio/dashboards');
      return resp.dashboards;
    },
  });
}

export function useCluvioFilterOptions(filterId: string) {
  return useQuery({
    queryKey: ['cluvio-filter-options', filterId],
    queryFn: async () => {
      const resp = await api.get<{ options: FilterOption[] }>(`/cluvio/filters/${filterId}/options`);
      return resp.options;
    },
    enabled: !!filterId,
  });
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const resp = await api.get<{ data: { listProfiles: { items: UserListItem[] } } }>('/profiles');
      return resp.data.listProfiles.items;
    },
    enabled,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const resp = await api.get<{ data: { getProfile: UserDetail } }>(`/profiles/${id}`);
      return resp.data.getProfile;
    },
    enabled: !!id,
  });
}

export function useUpdateProfile(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { firstname?: string; lastname?: string; roleDescription?: string; phone?: string }) => {
      return api.put(`/profiles/${userId}`, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', userId] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useResendInvitation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => {
      return api.post(`/profiles/${userId}/resend`, { email });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', userId] });
    },
  });
}

export function useInvalidateToken(userId: string) {
  return useMutation({
    mutationFn: async () => {
      return api.post(`/profiles/${userId}/invalidate-token`, {});
    },
  });
}

export function useRemoveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return api.del(`/profiles/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; firstname: string; lastname: string; tenantRole: string }) => {
      return api.post('/users/invite', input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/** Add one user to several tenants at once; reports per-tenant failures. */
export function useAddToTenants(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantIds, tenantTypes, role }: { tenantIds: string[]; tenantTypes: Record<string, string>; role: string }) => {
      const failed: string[] = [];
      for (const tid of tenantIds) {
        try {
          await api.post(`/tenants/${tid}/members`, {
            profileID: userId,
            tenantRole: role,
            typeOfTenantID: tenantTypes[tid],
          });
        } catch {
          failed.push(tid);
        }
      }
      return { failed };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', userId] });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['tenant'] });
    },
  });
}

/** Membership mutations scoped to a user detail view. */
export function useUserMembershipOps(userId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['user', userId] });
    qc.invalidateQueries({ queryKey: ['users'] });
  };
  const updateRole = useMutation({
    mutationFn: async ({ id, tenantRole }: { id: string; tenantRole: string }) => {
      return api.put(`/memberships/${id}`, { tenantRole });
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (membershipId: string) => {
      return api.del(`/memberships/${membershipId}`);
    },
    onSuccess: invalidate,
  });
  return { updateRole, remove };
}

/** Cognito groups + admin claims for all users. SuperAdmin-only upstream;
 *  callers should pass enabled=isSuperAdmin and treat errors as 'no badges'. */
export function useCognitoGroups(enabled: boolean) {
  return useQuery({
    queryKey: ['cognito-groups'],
    queryFn: async () => {
      const resp = await api.get<{
        users: Array<{ username: string; email: string | null; groups: string[]; typeOfTenants: string[] }>;
      }>('/cognito/groups');
      return resp.users;
    },
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch a signed S3 download URL for a tenant document. */
export function useDocumentDownload(tenantId: string) {
  return useMutation({
    mutationFn: async (doc: { documentID: string; privacyLevel: string; fileName?: string | null }) => {
      const resp = await api.post<{ data: { getDocumentsDownloadUrl: { signedUrl: string } } }>(
        '/documents/download',
        { tenantId, ...doc }
      );
      return resp.data.getDocumentsDownloadUrl.signedUrl;
    },
  });
}

export interface AuditItem {
  id: string;
  ts: string;
  actor?: string;
  /** Resolved server-side: display name / email of the actor, when known. */
  actorName?: string;
  actorEmail?: string;
  action: string;
  targetType: string;
  targetId?: string;
  detail?: string;
}

export function useAuditLog(filters: { actor?: string; action?: string; targetType?: string }, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['audit', filters],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '50' });
      if (filters.actor) params.set('actor', filters.actor);
      if (filters.action) params.set('action', filters.action);
      if (filters.targetType) params.set('targetType', filters.targetType);
      if (pageParam) params.set('cursor', pageParam as string);
      return api.get<{ items: AuditItem[]; nextCursor: string | null }>(`/audit?${params}`);
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

export function useCluvioFilters() {
  return useQuery({
    queryKey: ['cluvio-filters'],
    queryFn: async () => {
      const resp = await api.get<{ filters: Array<{ id: string; name: string; variable_name: string; controlType: string }> }>('/cluvio/filters');
      return resp.filters;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useGraphQLQuery() {
  return useMutation({
    mutationFn: async ({ query, variables }: { query: string; variables?: Record<string, unknown> }) => {
      return api.post<{ data: unknown; errors?: unknown[] }>('/graphql', { query, variables });
    },
  });
}
